import { Fragment, useEffect, useState } from "react";

import clsx from "clsx";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

import { type TopLevelBreakdown } from "../../types";

import { BreakdownValueSelectorContent } from "./breakdown-value-selector-content";

interface Props {
  filters: TopLevelBreakdown[];
  onUpdate(filter: TopLevelBreakdown): void;
  onDelete(filter: TopLevelBreakdown): void;
}

export const FiltersButtonCompact = ({
  filters,
  onUpdate,
  onDelete,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedBreakdown] =
    useState<TopLevelBreakdown | null>(null);

  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "bottom-end",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(() => {
    if (isOpen) {
      setSelectedBreakdown(null);
    }
  }, [isOpen]);

  return (
    <Popover>
      <PopoverButton
        as="button"
        className={clsx(
          "button sm secondary-button h-7 py-0 max-w-[106px] truncate",
          {
            "bg-cosmic-4 border-cosmic-4 hover:bg-cosmic-3 hover:border-cosmic-3":
              !isOpen,
            "bg-cosmic border-cosmic text-white": isOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        ({filters.length}) FILTERS
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
        show={isOpen}
      >
        <PopoverPanel
          ref={refs.setFloating}
          style={floatingStyles}
          className={clsx(
            "absolute z-10 bg-white font-sans rounded-lg elevation-1 text-sm",
            "pt-3 w-[250px]",
          )}
          {...getFloatingProps()}
        >
          {selectedFilter ? (
            <BreakdownValueSelectorContent
              breakdown={selectedFilter}
              onUpdate={(values) => {
                onUpdate({ key: selectedFilter.key, values });
                setIsOpen(false);
              }}
              onDelete={() => {
                onDelete(selectedFilter);
                setIsOpen(false);
              }}
            />
          ) : (
            <>
              {filters.map((e) => {
                const breakdownConfig = breakdownConfigs.find(
                  (v) => v.key === e.key,
                ) as CategoricalBreakdownConfig | undefined;

                if (!breakdownConfig) {
                  return null;
                }

                return (
                  <div key={e.key} className="mb-2 px-4 last:pb-2">
                    <button
                      className={clsx(
                        "button sm secondary-button h-7 py-0 max-w-[200px] truncate",
                        "bg-cosmic-4 border-cosmic-4 hover:bg-cosmic-3 hover:border-cosmic-3",
                      )}
                      onClick={() => setSelectedBreakdown(e)}
                    >
                      ({e.values.length}) {breakdownConfig.name}
                    </button>
                  </div>
                );
              })}
            </>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};
